import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  InputAdornment, Box, Typography, Chip, CircularProgress, Checkbox,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SearchNormal1 } from 'iconsax-react';
import { Task } from '../../types/task.type';
import { ObjectiveTask } from '../../types/objective.type';
import { objectiveTaskToTask } from '../../utils/objective.utils';
import { getWorkStatus, taskKey, WORK_STATUS_COLORS, workStatusLabelKey } from '../../utils/task.utils';

export interface NewObjectiveLink {
  google_task_id: string;
  owner_id: string;
  task_list_id: string;
  weight: number;
}

interface Props {
  open: boolean;
  tasks: Task[];
  linkedTasks: ObjectiveTask[];
  loading?: boolean;
  saving?: boolean;
  onClose: () => void;
  onSave: (links: NewObjectiveLink[]) => Promise<void>;
}

function splitEvenly(count: number, total: number): number[] {
  if (count <= 0 || total <= 0) return Array(count).fill(0);
  const base = Math.floor((total / count) * 10) / 10;
  const weights = Array(count).fill(base);
  const used = Math.round(base * count * 10) / 10;
  weights[count - 1] = Math.round((base + (total - used)) * 10) / 10;
  return weights;
}

export default function LinkTaskDialog({
  open,
  tasks,
  linkedTasks,
  loading,
  saving,
  onClose,
  onSave,
}: Props) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setSearch('');
    const initial: Record<string, string> = {};
    for (const linked of linkedTasks) {
      const key = `${linked.owner_id}:${linked.task_list_id}:${linked.google_task_id}`;
      initial[key] = linked.weight == null ? '' : String(linked.weight);
    }
    setSelected(initial);
    // Weights are captured when the dialog opens. Later parent renders must not reset the draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const catalog = useMemo(() => {
    const map = new Map<string, Task>();
    for (const task of tasks) map.set(taskKey(task), task);
    for (const linked of linkedTasks) {
      const asTask = objectiveTaskToTask(linked);
      const key = taskKey(asTask);
      if (!map.has(key)) map.set(key, asTask);
    }
    return Array.from(map.values());
  }, [tasks, linkedTasks]);

  const availableTasks = useMemo(() => {
    const q = search.trim().toLowerCase();
    return catalog.filter(task => {
      if (selected[taskKey(task)] !== undefined) return true;
      if (!q) return true;
      return task.title.toLowerCase().includes(q) || task.employee_name.toLowerCase().includes(q);
    });
  }, [catalog, search, selected]);

  const orderedTasks = useMemo(() => {
    return [...availableTasks].sort((a, b) => {
      const aSelected = selected[taskKey(a)] !== undefined;
      const bSelected = selected[taskKey(b)] !== undefined;
      if (aSelected === bSelected) return 0;
      return aSelected ? -1 : 1;
    });
  }, [availableTasks, selected]);

  const selectedTasks = useMemo(
    () => catalog.filter(task => selected[taskKey(task)] !== undefined),
    [catalog, selected],
  );

  const selectedSum = selectedTasks.reduce((sum, task) => {
    const n = Number(selected[taskKey(task)]);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
  const total = Math.round(selectedSum * 10) / 10;
  const remainder = Math.max(0, Math.round((100 - total) * 10) / 10);
  const totalOk = Math.abs(total - 100) < 0.001;
  const canSave = selectedTasks.length > 0 && totalOk && !saving;

  const toggle = (task: Task) => {
    const key = taskKey(task);
    setSelected(prev => {
      if (prev[key] !== undefined) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: '' };
    });
  };

  const setWeight = (task: Task, value: string) => {
    setSelected(prev => ({ ...prev, [taskKey(task)]: value }));
  };

  const handleSplit = () => {
    const keys = Object.keys(selected);
    const parts = splitEvenly(keys.length, 100);
    setSelected(prev => {
      const next = { ...prev };
      keys.forEach((key, index) => {
        next[key] = String(parts[index]);
      });
      return next;
    });
  };

  const handleSave = async () => {
    if (!canSave) return;
    const links: NewObjectiveLink[] = selectedTasks.map(task => ({
      google_task_id: task.google_task_id,
      owner_id: task.employee_id,
      task_list_id: task.task_list_id,
      weight: Number(selected[taskKey(task)]),
    }));
    await onSave(links);
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>{t('objectives.linkTasks')}</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
          px: 1.5,
          py: 1,
          borderRadius: 2,
          bgcolor: totalOk ? '#F0FDF4' : '#F8FAFC',
          border: '1px solid',
          borderColor: totalOk ? '#BBF7D0' : '#E2E8F0',
        }}>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{t('objectives.remainingWeight', { remainder })}</Typography>
          <Typography sx={{ fontWeight: 700, color: totalOk ? '#16A34A' : '#111827' }}>
            {t('objectives.weightSum', { sum: total })}
          </Typography>
        </Box>

        <TextField
          fullWidth
          size="small"
          placeholder={t('objectives.searchTasks')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchNormal1 size={16} color="#94A3B8" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1.5 }}
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : availableTasks.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              {t('objectives.noTasksToLink')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 380, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {selectedTasks.length > 0 && (
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary', px: 0.5 }}>
                {t('objectives.selectedTasks', { count: selectedTasks.length })}
              </Typography>
            )}
            {orderedTasks.map(task => {
              const key = taskKey(task);
              const isSelected = selected[key] !== undefined;
              const status = getWorkStatus(task);
              const colors = WORK_STATUS_COLORS[status];
              return (
                <Box
                  key={key}
                  onClick={() => toggle(task)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1,
                    py: 1,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: isSelected ? '#C7D2FE' : '#E5E7EB',
                    bgcolor: isSelected ? '#F5F7FF' : '#fff',
                    cursor: 'pointer',
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={isSelected}
                    tabIndex={-1}
                    disableRipple
                    sx={{ p: 0.5 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 600 }}>{task.title}</Typography>
                    <Typography noWrap sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{task.employee_name}</Typography>
                  </Box>
                  <Chip
                    label={t(workStatusLabelKey(status))}
                    size="small"
                    sx={{ bgcolor: colors.bg, color: colors.color, fontSize: '0.7rem', height: 22, border: 'none' }}
                  />
                  {isSelected && (
                    <TextField
                      size="small"
                      type="number"
                      value={selected[key]}
                      onClick={e => e.stopPropagation()}
                      onChange={e => setWeight(task, e.target.value)}
                      placeholder="0"
                      inputProps={{ min: 0, max: 100, step: 1 }}
                      sx={{ width: 84 }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'space-between' }}>
        <Button
          onClick={handleSplit}
          disabled={selectedTasks.length === 0 || !!saving}
          sx={{ textTransform: 'none' }}
        >
          {t('objectives.splitEvenly')}
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} disabled={!!saving} sx={{ textTransform: 'none' }}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={!canSave} sx={{ textTransform: 'none' }}>
            {saving ? t('common.loading') : t('objectives.saveLinks')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
